import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useMention, MentionDropdown } from '../components/MentionInput';
import { usePresenceContext, SCREEN_LABELS } from '../hooks/usePresence';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

const TEAM_COLORS = [
  { color: '#314F5C', text: '#AFE1F6' }, { color: '#819D84', text: '#fff' },
  { color: '#B99C6D', text: '#fff' },    { color: '#6F8FA7', text: '#fff' },
];
function makeTeamMember(email, idx) {
  const parts = (email || '').split('@')[0].split(/[._-]/);
  const name = parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  const initials = parts.map(p => p[0]?.toUpperCase()).join('').slice(0, 2) || '??';
  return { initials, name, email, ...TEAM_COLORS[idx % TEAM_COLORS.length] };
}

const STATUS_CFG = {
  open:        { bg: '#fff3e0', color: '#7d4e00', label: 'Open' },
  in_progress: { bg: '#e3f0ff', color: '#1a4a8a', label: 'In progress' },
  resolved:    { bg: '#e8f5e9', color: '#1b5e20', label: 'Resolved' },
};

// Tag config — matches the FeedbackModal tag chips in sanoire-native.
// Critical is intentionally red so it stands out in the ticket list.
const TAG_CFG = {
  bug:      { bg: '#fde8e8', color: '#9b1c1c', emoji: 'BUG',     label: 'Bug' },
  idea:     { bg: '#e0f2fe', color: '#0c4a6e', emoji: 'IDEA',    label: 'Idea' },
  praise:   { bg: '#dcfce7', color: '#14532d', emoji: 'PRAISE',  label: 'Praise' },
  confused: { bg: '#fef3c7', color: '#78350f', emoji: 'HUH',     label: 'Confused' },
  critical: { bg: '#fecaca', color: '#7f1d1d', emoji: 'URGENT',  label: 'Critical' },
};

// Source config — visually distinguish in-app feedback from email/web tickets.
const SOURCE_CFG = {
  in_app_feedback: { label: 'in-app',  color: '#7a5a1a' },
  support:         { label: 'support', color: '#6F8FA7' },
};

// Platform glyph for the device chip.
const PLATFORM_GLYPH = { ios: 'iOS', android: 'Android', web: 'Web' };

function timeAgo(ts) {
  if (!ts) return '';
  const m = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h ago';
  return Math.floor(h / 24) + 'd ago';
}

function Avatar({ initials, color, textColor, size = 28 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: size < 24 ? '50%' : 9, background: color,
      color: textColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38 + 'px', fontWeight: 700, flexShrink: 0 }}>
      {initials}
    </div>
  );
}

function TelegramBadge({ handle }) {
  if (!handle) return null;
  const clean = handle.startsWith('@') ? handle : '@' + handle;
  return (
    <a href={'https://t.me/' + clean.replace('@', '')} target="_blank" rel="noreferrer"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#e3f0fb',
        border: '1px solid #b0cfe8', borderRadius: 5, padding: '1px 6px',
        fontSize: '.6rem', color: '#1a5ca8', textDecoration: 'none' }}>
      <svg width="9" height="9" viewBox="0 0 24 24" fill="#1a5ca8"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248l-2.04 9.602c-.148.658-.537.818-1.084.508l-3-2.21-1.447 1.392c-.16.16-.295.295-.605.295l.216-3.053 5.56-5.023c.242-.215-.053-.334-.372-.12l-6.87 4.326-2.962-.924c-.643-.203-.657-.643.136-.952l11.57-4.463c.537-.194 1.006.13.898.622z"/></svg>
      {clean}
    </a>
  );
}

export default function Support({ onNav }) {
  const onlineUsers = usePresenceContext();
  const [tickets, setTickets]     = useState([]);
  const [selected, setSelected]   = useState(null);
  const [replies, setReplies]     = useState([]);
  const [notes, setNotes]         = useState([]);
  const [viewFilter, setViewFilter] = useState('unassigned');
  const [statusFilter, setStatusFilter] = useState('open');
  const [tagFilter, setTagFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('customer'); // 'customer' | 'internal'
  const [replyText, setReplyText] = useState('');
  const [noteText, setNoteText]   = useState('');
  const [sending, setSending]     = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [loading, setLoading]     = useState(true);
  const [me, setMe]               = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [reassigning, setReassigning] = useState(false);
  const [isMobile, setIsMobile]   = useState(() => window.innerWidth < 768);
  const bottomRef = useRef(null);

  const replyMention = useMention(replyText, e => setReplyText(e.target.value), teamMembers);
  const noteMention  = useMention(noteText,  e => setNoteText(e.target.value),  teamMembers);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (!data?.user) return;
      const email = data.user.email || '';
      const parts = email.split('@')[0].split(/[._-]/);
      const name  = parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
      const initials = parts.map(p => p[0]?.toUpperCase()).join('').slice(0, 2) || '??';
      setMe({ initials, name, email, color: '#314F5C', text: '#AFE1F6' });
    });
    supabase.rpc('admin_get_auth_users').then(({ data }) => {
      if (data) setTeamMembers(data.filter(u => !!u.last_sign_in_at).map((u, i) => makeTeamMember(u.email, i)));
    });
  }, []);

  const loadTickets = useCallback(async () => {
    const { data } = await supabase.rpc('admin_get_tickets');
    setTickets(data || []);
    setLoading(false);
  }, []);

  const loadReplies = useCallback(async (id) => {
    const { data } = await supabase.rpc('admin_get_replies', { p_ticket_id: id });
    setReplies(data || []);
  }, []);

  const loadNotes = useCallback(async (id) => {
    const { data } = await supabase.rpc('admin_get_notes', { p_ticket_id: id });
    setNotes(data || []);
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  useEffect(() => {
    if (!selected) return;
    loadReplies(selected.id);
    loadNotes(selected.id);
    const ch = supabase.channel('replies-' + selected.id)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'support_replies', filter: 'ticket_id=eq.' + selected.id },
        () => loadReplies(selected.id))
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'support_internal_notes', filter: 'ticket_id=eq.' + selected.id },
        () => loadNotes(selected.id))
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [selected, loadReplies, loadNotes]);

  useEffect(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, [replies, notes, activeTab]);

  const filtered = tickets.filter(t => {
    const statusOk = statusFilter === 'all' || t.status === statusFilter ||
      (statusFilter === 'open' && (t.status === 'open' || t.status === 'in_progress'));
    const viewOk = viewFilter === 'all' ? true :
      viewFilter === 'mine' ? t.assigned_to_email === me?.email :
      viewFilter === 'unassigned' ? !t.assigned_to : true;
    const tagOk = tagFilter === 'all' ? true : t.tag === tagFilter;
    return statusOk && viewOk && tagOk;
  });

  const unassignedCount = tickets.filter(t => !t.assigned_to && t.status !== 'resolved').length;
  const mineCount = me ? tickets.filter(t => t.assigned_to_email === me.email && t.status !== 'resolved').length : 0;

  async function handleClaim() {
    if (!selected || !me) return;
    try {
      await supabase.rpc('admin_claim_ticket', { p_ticket_id: selected.id, p_assignee: me.name, p_assignee_email: me.email });
      setSelected(prev => ({ ...prev, assigned_to: me.name, assigned_to_email: me.email, status: prev.status === 'open' ? 'in_progress' : prev.status }));
      setTickets(prev => prev.map(t => t.id === selected.id ? { ...t, assigned_to: me.name, assigned_to_email: me.email } : t));
    } catch (_e) { console.error('Support error:', _e); }
  }

  async function handleAssign(member) {
    if (!selected) return;
    setReassigning(false);
    try {
      if (member === '__unassign') {
        await supabase.rpc('admin_unassign_ticket', { p_ticket_id: selected.id });
        setSelected(prev => ({ ...prev, assigned_to: null, assigned_to_email: null }));
        setTickets(prev => prev.map(t => t.id === selected.id ? { ...t, assigned_to: null, assigned_to_email: null } : t));
      } else {
        await supabase.rpc('admin_assign_ticket', { p_ticket_id: selected.id, p_assignee: member.name, p_assignee_email: member.email });
        setSelected(prev => ({ ...prev, assigned_to: member.name, assigned_to_email: member.email }));
        setTickets(prev => prev.map(t => t.id === selected.id ? { ...t, assigned_to: member.name, assigned_to_email: member.email } : t));
      }
    } catch (_e) { console.error('Support error:', _e); }
  }

  async function handleReply() {
    if (!replyText.trim() || !selected || sending) return;
    setSending(true);
    try {
      await supabase.rpc('admin_reply', { p_ticket_id: selected.id, p_message: replyText.trim() });
      setReplyText('');
      await loadReplies(selected.id);
      // Update status optimistically instead of re-fetching all tickets
      setTickets(prev => prev.map(t =>
        t.id === selected.id && t.status === 'open' ? { ...t, status: 'in_progress' } : t
      ));
      setSelected(prev => ({ ...prev, status: prev.status === 'open' ? 'in_progress' : prev.status }));
    } catch (_e) {
      // Reply failed — UI stays consistent, user can retry
    } finally {
      setSending(false);
    }
  }

  async function handleAddNote() {
    if (!noteText.trim() || !selected || !me || addingNote) return;
    setAddingNote(true);
    try {
      await supabase.rpc('admin_add_note', { p_ticket_id: selected.id, p_author: me.name, p_author_email: me.email, p_message: noteText.trim() });
      setNoteText('');
      await loadNotes(selected.id);
    } catch (_e) { console.error('Support error:', _e); } finally {
      setAddingNote(false);
    }
  }

  async function handleStatus(status) {
    if (!selected) return;
    try {
      await supabase.rpc('admin_update_ticket_status', { p_ticket_id: selected.id, p_status: status });
      setSelected(prev => ({ ...prev, status }));
      setTickets(prev => prev.map(t => t.id === selected.id ? { ...t, status } : t));
    } catch (_e) { console.error('Support error:', _e); }
  }

  const assigneeMember = selected?.assigned_to_email
    ? (teamMembers.find(t => t.email === selected.assigned_to_email) || makeTeamMember(selected.assigned_to_email, 0))
    : null;

  // ── TICKET LIST SIDEBAR ─────────────────────────────────────────────────────
  const sidebar = (
    <div style={{ width: isMobile ? '100%' : 280, flexShrink: 0, background: '#edf2f5',
      borderRight: '1px solid rgba(49,79,92,0.08)', display: 'flex', flexDirection: 'column',
      minHeight: 0, fontFamily: F }}>

      {/* Header */}
      <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid rgba(49,79,92,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: '.8rem', fontWeight: 700, color: '#0f1e28', letterSpacing: '-.01em' }}>Customer Help</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: -4 }}>
            {teamMembers.filter(t => !!onlineUsers[t.initials]).slice(0, 3).map((t, i) => (
              <div key={t.initials} title={t.name + ' · ' + (SCREEN_LABELS[onlineUsers[t.initials]?.screen] || 'Online')}
                style={{ marginLeft: i > 0 ? -6 : 0, zIndex: 3 - i, position: 'relative',
                  width: 20, height: 20, borderRadius: '50%', background: t.color, color: t.text,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '.5rem', fontWeight: 700, border: '1.5px solid #edf2f5' }}>
                {t.initials}
                <div style={{ position: 'absolute', bottom: -1, right: -1, width: 6, height: 6, borderRadius: '50%',
                  background: '#2da06a', border: '1px solid #edf2f5' }} />
              </div>
            ))}
            {Object.keys(onlineUsers).length === 0 && (
              <span style={{ fontSize: '.58rem', color: '#9eb8c8' }}>No one online</span>
            )}
          </div>
        </div>

        {/* View filter */}
        <div style={{ display: 'flex', gap: 3, marginBottom: 6 }}>
          {[
            { key: 'mine',       label: mineCount > 0 ? 'Mine (' + mineCount + ')' : 'Mine' },
            { key: 'unassigned', label: unassignedCount > 0 ? 'New (' + unassignedCount + ')' : 'New' },
            { key: 'all',        label: 'All' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setViewFilter(key)} style={{
              flex: 1, padding: '4px 0', borderRadius: 20, fontFamily: F, cursor: 'pointer',
              fontSize: '.6rem', fontWeight: 600,
              background: viewFilter === key
                ? (key === 'unassigned' && unassignedCount > 0 ? '#e53935' : '#314F5C')
                : 'rgba(49,79,92,0.06)',
              color: viewFilter === key ? '#fff'
                : (key === 'unassigned' && unassignedCount > 0 ? '#e53935' : '#5a7a8a'),
              border: 'none',
            }}>{label}</button>
          ))}
        </div>

        {/* Status filter */}
        <div style={{ display: 'flex', gap: 3 }}>
          {[['open','Open'],['resolved','Resolved'],['all','All']].map(([key, label]) => (
            <button key={key} onClick={() => setStatusFilter(key)} style={{
              flex: 1, padding: '3px 0', borderRadius: 5, border: 'none', cursor: 'pointer', fontFamily: F,
              fontSize: '.58rem', fontWeight: statusFilter === key ? 600 : 400,
              background: statusFilter === key ? 'rgba(49,79,92,0.1)' : 'transparent',
              color: statusFilter === key ? '#314F5C' : '#9eb8c8',
            }}>{label}</button>
          ))}
        </div>

        {/* Tag filter — segmented control. 'All' shows everything; clicking a
            tag narrows to feedback of that kind. Counts in parens so admins
            can see distribution at a glance. */}
        <div style={{ display: 'flex', gap: 3, marginTop: 6, flexWrap: 'wrap' }}>
          {[
            ['all', 'All', tickets.length],
            ['bug', 'Bug', tickets.filter(t => t.tag === 'bug').length],
            ['idea', 'Idea', tickets.filter(t => t.tag === 'idea').length],
            ['praise', 'Praise', tickets.filter(t => t.tag === 'praise').length],
            ['confused', 'Confused', tickets.filter(t => t.tag === 'confused').length],
            ['critical', 'Critical', tickets.filter(t => t.tag === 'critical').length],
          ].map(([key, label, count]) => {
            const active = tagFilter === key;
            const isCritical = key === 'critical' && count > 0;
            return (
              <button key={key} onClick={() => setTagFilter(key)} style={{
                padding: '3px 7px', borderRadius: 99, border: 'none', cursor: 'pointer', fontFamily: F,
                fontSize: '.55rem', fontWeight: active ? 600 : 500,
                background: active
                  ? (isCritical ? '#fecaca' : 'rgba(49,79,92,0.1)')
                  : (isCritical ? 'rgba(254,202,202,0.4)' : 'transparent'),
                color: active
                  ? (isCritical ? '#7f1d1d' : '#314F5C')
                  : (isCritical ? '#9b1c1c' : '#9eb8c8'),
              }}>{label}{count > 0 ? ' (' + count + ')' : ''}</button>
            );
          })}
        </div>
      </div>

      {/* Ticket list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 8px' }}>
        {loading && <div style={{ padding: 20, textAlign: 'center', color: '#9eb8c8', fontSize: '.72rem' }}>Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#9eb8c8', fontSize: '.72rem' }}>No tickets</div>
        )}
        {filtered.map(t => {
          const active    = selected?.id === t.id;
          const assignee  = teamMembers.find(m => m.email === t.assigned_to_email);
          const cfg       = STATUS_CFG[t.status] || STATUS_CFG.open;
          return (
            <button key={t.id}
              onClick={() => { setSelected(t); setActiveTab('customer'); setReassigning(false); }}
              style={{ width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none',
                fontFamily: F, cursor: 'pointer', borderRadius: 10, marginBottom: 2,
                background: active ? '#fff' : 'transparent',
                boxShadow: active ? '0 1px 4px rgba(49,79,92,0.1)' : 'none',
                transition: 'all .12s' }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '.58rem', fontWeight: 700, padding: '2px 6px', borderRadius: 99,
                    background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                  {t.tag && TAG_CFG[t.tag] && (
                    <span style={{ fontSize: '.55rem', fontWeight: 700, padding: '2px 6px', borderRadius: 99,
                      background: TAG_CFG[t.tag].bg, color: TAG_CFG[t.tag].color, letterSpacing: '.04em' }}>
                      {TAG_CFG[t.tag].emoji}
                    </span>
                  )}
                  {t.source && SOURCE_CFG[t.source] && t.source !== 'support' && (
                    <span style={{ fontSize: '.52rem', fontWeight: 600, padding: '1px 5px', borderRadius: 4,
                      background: 'transparent', color: SOURCE_CFG[t.source].color,
                      border: '1px solid ' + SOURCE_CFG[t.source].color, letterSpacing: '.04em',
                      textTransform: 'uppercase' }}>
                      {SOURCE_CFG[t.source].label}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: '.58rem', color: '#a0b4be', flexShrink: 0 }}>{timeAgo(t.created_at)}</span>
              </div>
              <div style={{ fontSize: '.76rem', fontWeight: active ? 600 : 500,
                color: t.status === 'resolved' ? '#a0b4be' : '#0f1e28',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 }}>
                {t.subject || t.message?.slice(0, 50) || 'No subject'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                <span style={{ fontSize: '.62rem', color: '#7a9aaa', overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.email}</span>
                {!t.assigned_to
                  ? <span style={{ fontSize: '.58rem', fontWeight: 700, color: '#e53935', flexShrink: 0 }}>New</span>
                  : <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                      {assignee && <Avatar initials={assignee.initials} color={assignee.color}
                        textColor={assignee.text} size={14} />}
                      <span style={{ fontSize: '.58rem', color: '#314F5C', fontWeight: 500 }}>{t.assigned_to?.split(' ')[0]}</span>
                    </div>
                }
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  // ── MAIN PANEL ──────────────────────────────────────────────────────────────
  const mainPanel = (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff',
      overflow: 'hidden', minWidth: 0, fontFamily: F }}>

      {!selected ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', color: '#9eb8c8', fontSize: '.78rem', gap: 8 }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d0dde4" strokeWidth="1.2" strokeLinecap="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
          </svg>
          Select a ticket
        </div>
      ) : (
        <>
          {/* Header */}
          <div style={{ padding: '11px 18px', borderBottom: '1px solid #edf0f3', background: '#fff',
            flexShrink: 0 }}>
            {isMobile && (
              <button onClick={() => { setSelected(null); setReplies([]); setNotes([]); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#314F5C',
                  fontSize: '.72rem', marginBottom: 8, padding: 0, fontFamily: F, display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
                All tickets
              </button>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                  <span style={{ fontSize: '.88rem', fontWeight: 600, color: '#0f1e28',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selected.subject || 'Support ticket'}
                  </span>
                  <span style={{ fontSize: '.58rem', fontWeight: 700, padding: '2px 7px', borderRadius: 99, flexShrink: 0,
                    background: (STATUS_CFG[selected.status] || STATUS_CFG.open).bg,
                    color: (STATUS_CFG[selected.status] || STATUS_CFG.open).color }}>
                    {(STATUS_CFG[selected.status] || STATUS_CFG.open).label}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '.66rem', color: '#7a9aaa' }}>{selected.email}</span>
                  <span style={{ fontSize: '.66rem', color: '#aab8c0' }}>· {timeAgo(selected.created_at)}</span>
                  {selected.telegram && <TelegramBadge handle={selected.telegram} />}
                  {/* Assignee */}
                  {selected.assigned_to ? (
                    <div style={{ position: 'relative' }}>
                      <div onClick={() => setReassigning(r => !r)}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                          padding: '2px 7px', borderRadius: 99, background: 'rgba(49,79,92,0.07)',
                          border: '1px solid rgba(49,79,92,0.12)' }}>
                        {assigneeMember && <Avatar initials={assigneeMember.initials}
                          color={assigneeMember.color} textColor={assigneeMember.text} size={14} />}
                        <span style={{ fontSize: '.62rem', color: '#314F5C', fontWeight: 600 }}>{selected.assigned_to?.split(' ')[0]}</span>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#6F8FA7" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                      </div>
                      {reassigning && (
                        <div style={{ position: 'absolute', top: '110%', left: 0, background: '#fff',
                          border: '1px solid #e4edf3', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          zIndex: 100, minWidth: 160, padding: 6 }}>
                          {teamMembers.map(m => (
                            <button key={m.email} onClick={() => handleAssign(m)}
                              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                                padding: '7px 10px', border: 'none', background: 'transparent',
                                cursor: 'pointer', borderRadius: 7, fontFamily: F }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f5f7f8'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                              <Avatar initials={m.initials} color={m.color} textColor={m.text} size={18} />
                              <span style={{ fontSize: '.74rem', color: '#0f1e28' }}>{m.name}</span>
                            </button>
                          ))}
                          <div style={{ borderTop: '1px solid #f0f2f4', margin: '4px 0' }} />
                          <button onClick={() => handleAssign('__unassign')}
                            style={{ width: '100%', padding: '6px 10px', border: 'none', background: 'transparent',
                              cursor: 'pointer', fontSize: '.68rem', color: '#9eb8c8', textAlign: 'left',
                              fontFamily: F, borderRadius: 7 }}>
                            Unassign
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button onClick={handleClaim}
                      style={{ padding: '2px 9px', background: '#314F5C', color: '#E0F5FC',
                        border: 'none', borderRadius: 99, fontSize: '.6rem', fontWeight: 600,
                        cursor: 'pointer', fontFamily: F }}>
                      Claim
                    </button>
                  )}
                  {/* Open in Dispatch */}
                  {onNav && (
                    <button onClick={() => onNav('customerhelp')}
                      style={{ padding: '2px 8px', background: 'transparent', border: '1px solid #e4edf3',
                        borderRadius: 99, fontSize: '.6rem', color: '#6F8FA7', cursor: 'pointer',
                        fontFamily: F, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                      </svg>
                      Dispatch
                    </button>
                  )}
                </div>
              </div>
              {/* Status actions */}
              <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                {selected.status !== 'in_progress' && selected.status !== 'resolved' && (
                  <button onClick={() => handleStatus('in_progress')}
                    style={{ padding: '4px 10px', background: '#6F8FA7', color: '#fff', border: 'none',
                      borderRadius: 7, fontSize: '.66rem', fontWeight: 600, cursor: 'pointer', fontFamily: F }}>
                    Start
                  </button>
                )}
                {selected.status !== 'resolved' && (
                  <button onClick={() => handleStatus('resolved')}
                    style={{ padding: '4px 10px', background: '#819D84', color: '#fff', border: 'none',
                      borderRadius: 7, fontSize: '.66rem', fontWeight: 600, cursor: 'pointer', fontFamily: F }}>
                    Resolve
                  </button>
                )}
                {selected.status === 'resolved' && (
                  <button onClick={() => handleStatus('open')}
                    style={{ padding: '4px 10px', background: 'transparent', color: '#9eb8c8',
                      border: '1px solid #d8e4ea', borderRadius: 7, fontSize: '.66rem',
                      cursor: 'pointer', fontFamily: F }}>
                    Reopen
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tabs — styled like Dispatch */}
          <div style={{ display: 'flex', borderBottom: '1px solid #edf0f3', flexShrink: 0, background: '#fff' }}>
            {[
              { key: 'customer', label: 'Customer thread' },
              { key: 'internal', label: 'Internal · ' + (notes.length || '0') },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setActiveTab(key)}
                style={{ flex: 1, padding: '8px 0', fontSize: '.68rem', fontWeight: activeTab === key ? 600 : 400,
                  color: activeTab === key ? '#314F5C' : '#9eb8c8', background: 'none', border: 'none',
                  borderBottom: activeTab === key ? '2px solid #314F5C' : '2px solid transparent',
                  cursor: 'pointer', fontFamily: F, transition: 'all .12s' }}>
                {label}
              </button>
            ))}
          </div>

          {/* Customer thread */}
          {activeTab === 'customer' && (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0', display: 'flex',
                flexDirection: 'column', gap: 0, background: '#fff' }}>
                {/* Original message */}
                <div style={{ display: 'flex', gap: 10, padding: '8px 20px 4px', alignItems: 'flex-start' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 11, background: '#AFE1F6',
                    color: '#314F5C', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '.6rem', fontWeight: 700, flexShrink: 0 }}>
                    {(selected.email || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: '.82rem', fontWeight: 600, color: '#0f1e28' }}>{selected.email?.split('@')[0]}</span>
                      <span style={{ fontSize: '.62rem', color: '#9eb8c8' }}>{timeAgo(selected.created_at)}</span>
                    </div>
                    <div style={{ fontSize: '.82rem', color: '#1a2e38', lineHeight: 1.6 }}>{selected.message}</div>
                  </div>
                </div>
                {/* Replies */}
                {replies.map((r, i) => {
                  const isAdmin = r.author === 'admin';
                  const member  = isAdmin
                    ? (teamMembers.find(m => m.email === r.author_email) || me || { initials: 'SA', color: '#314F5C', text: '#AFE1F6', name: 'Support' })
                    : null;
                  const showHead = i === 0 || (replies[i-1]?.author !== r.author);
                  return (
                    <div key={r.id} style={{ display: 'flex', gap: 10,
                      padding: showHead ? '8px 20px 2px' : '2px 20px',
                      alignItems: 'flex-start' }}>
                      <div style={{ width: 36, flexShrink: 0 }}>
                        {showHead ? (
                          isAdmin
                            ? <Avatar initials={member.initials} color={member.color} textColor={member.text} size={36} />
                            : <div style={{ width: 36, height: 36, borderRadius: 11, background: '#AFE1F6',
                                color: '#314F5C', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '.6rem', fontWeight: 700 }}>
                                {(r.author_email || selected.email || '?')[0].toUpperCase()}
                              </div>
                        ) : null}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {showHead && (
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                            <span style={{ fontSize: '.82rem', fontWeight: 600, color: '#0f1e28' }}>
                              {isAdmin ? (member.name || 'Support') : (r.author_email?.split('@')[0] || 'Customer')}
                            </span>
                            <span style={{ fontSize: '.62rem', color: '#9eb8c8' }}>{timeAgo(r.created_at)}</span>
                          </div>
                        )}
                        <div style={{ fontSize: '.82rem', color: '#1a2e38', lineHeight: 1.6 }}>{r.message}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              {/* Reply input — Dispatch style */}
              <div style={{ padding: '8px 18px 14px', borderTop: '1px solid #edf0f3', background: '#fff', flexShrink: 0 }}>
                <div style={{ background: '#f4f7f9', borderRadius: 13, border: '1px solid #e4edf3', overflow: 'hidden' }}>
                  <div style={{ position: 'relative' }}>
                    <MentionDropdown open={replyMention.open} filtered={replyMention.filtered}
                      idx={replyMention.idx} onSelect={replyMention.selectMention} />
                    <textarea ref={replyMention.inputRef} value={replyText}
                      onChange={replyMention.handleChange}
                      onKeyDown={e => { if (replyMention.handleKeyDown(e)) return; if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleReply(); }}
                      placeholder={'Reply to ' + selected.email + '… (⌘↵ to send, @ to mention)'}
                      rows={2}
                      style={{ width: '100%', border: 'none', background: 'transparent', padding: '10px 14px',
                        fontSize: '.82rem', fontFamily: F, color: '#0f1e28', resize: 'none', outline: 'none',
                        lineHeight: 1.55 }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 10px 8px', borderTop: '1px solid #e4edf3' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {selected.telegram && (
                        <a href={'https://t.me/' + selected.telegram.replace('@', '')} target="_blank" rel="noreferrer"
                          style={{ padding: '3px 9px', borderRadius: 6, background: 'rgba(26,92,168,0.07)',
                            color: '#1a5ca8', fontSize: '.66rem', textDecoration: 'none', fontFamily: F }}>
                          + Telegram ↗
                        </a>
                      )}
                    </div>
                    <button onClick={handleReply} disabled={!replyText.trim() || sending}
                      style={{ width: 32, height: 32, borderRadius: 9, border: 'none',
                        background: replyText.trim() ? '#314F5C' : '#e4edf3',
                        color: replyText.trim() ? '#E0F5FC' : '#9eb8c8',
                        cursor: replyText.trim() ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all .15s', flexShrink: 0 }}>
                      {sending
                        ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/></svg>
                        : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                      }
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Internal — Dispatch-style team chat */}
          {activeTab === 'internal' && (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0', display: 'flex',
                flexDirection: 'column', gap: 0, background: '#fff' }}>
                {notes.length === 0 && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', color: '#9eb8c8', gap: 6, padding: '32px 0' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d0dde4" strokeWidth="1.2" strokeLinecap="round">
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
                    </svg>
                    <span style={{ fontSize: '.72rem' }}>No internal notes yet</span>
                    <span style={{ fontSize: '.62rem', color: '#b0c4cc' }}>Only your team can see this</span>
                  </div>
                )}
                {notes.map((n, i) => {
                  const author   = teamMembers.find(m => m.email === n.author_email)
                    || (me?.email === n.author_email ? me : null)
                    || makeTeamMember(n.author_email || n.author, i);
                  const showHead = i === 0 || notes[i-1]?.author_email !== n.author_email;
                  return (
                    <div key={n.id} style={{ display: 'flex', gap: 10,
                      padding: showHead ? '8px 20px 2px' : '2px 20px',
                      alignItems: 'flex-start' }}>
                      <div style={{ width: 36, flexShrink: 0 }}>
                        {showHead && <Avatar initials={author.initials} color={author.color} textColor={author.text} size={36} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {showHead && (
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                            <span style={{ fontSize: '.82rem', fontWeight: 600, color: '#0f1e28' }}>{author.name}</span>
                            <span style={{ fontSize: '.62rem', color: '#9eb8c8' }}>{timeAgo(n.created_at)}</span>
                            <span style={{ fontSize: '.56rem', fontWeight: 600, padding: '1px 5px', borderRadius: 99,
                              background: '#fef9ef', color: '#d97706', border: '1px solid rgba(217,119,6,0.2)' }}>
                              INTERNAL
                            </span>
                          </div>
                        )}
                        <div style={{ fontSize: '.82rem', color: '#1a2e38', lineHeight: 1.6 }}>{n.message}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              {/* Internal note input — same Dispatch style */}
              <div style={{ padding: '8px 18px 14px', borderTop: '1px solid #edf0f3',
                background: 'rgba(254,249,239,0.6)', flexShrink: 0 }}>
                <div style={{ background: '#fff', borderRadius: 13, border: '1px solid rgba(217,119,6,0.2)',
                  overflow: 'hidden', boxShadow: '0 0 0 2px rgba(217,119,6,0.06)' }}>
                  <div style={{ position: 'relative' }}>
                    <MentionDropdown open={noteMention.open} filtered={noteMention.filtered}
                      idx={noteMention.idx} onSelect={noteMention.selectMention} />
                    <textarea ref={noteMention.inputRef} value={noteText}
                      onChange={noteMention.handleChange}
                      onKeyDown={e => { if (noteMention.handleKeyDown(e)) return; if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddNote(); }}
                      placeholder="Internal note — customer never sees this… (⌘↵ to send)"
                      rows={2}
                      style={{ width: '100%', border: 'none', background: 'transparent', padding: '10px 14px',
                        fontSize: '.82rem', fontFamily: F, color: '#0f1e28', resize: 'none', outline: 'none',
                        lineHeight: 1.55 }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 10px 8px', borderTop: '1px solid rgba(217,119,6,0.12)' }}>
                    <span style={{ fontSize: '.6rem', color: '#d97706', fontStyle: 'italic' }}>Only visible to your team</span>
                    <button onClick={handleAddNote} disabled={!noteText.trim() || addingNote || !me}
                      style={{ width: 32, height: 32, borderRadius: 9, border: 'none',
                        background: noteText.trim() ? '#d97706' : '#e4edf3',
                        color: noteText.trim() ? '#fff' : '#9eb8c8',
                        cursor: noteText.trim() ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all .15s', flexShrink: 0 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  // ── Layout ──────────────────────────────────────────────────────────────────
  if (isMobile) {
    if (selected) return <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff', minHeight: 0 }}>{mainPanel}</div>;
    return <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#edf2f5', minHeight: 0 }}>{sidebar}</div>;
  }
  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {sidebar}
      {mainPanel}
    </div>
  );
}
